import React, { useState } from 'react';
import { Modal, Tabs } from 'antd';
import { useThemeSwitcher } from 'react-css-theme-switcher';

import doReturnTheme from '~src/styles/themeEngine';
import icon from '~components/_common/icon/icon';

import { useIntl } from 'react-intl';
import moment from 'moment';

import { appLog } from '~src/utils/log';
import {
  loadFilesActionLog,
  loadFolderPermissionLog,
  loadFoldersLog,
} from '~src/services/api/actionLogs';
import { useProjectId } from '~src/hooks/useProjectId';
import { FileLog } from './FileLog';
import { FolderLog } from './FolderLog';
import { FolderPermissionsLog } from './FolderPermissionsLog';

const { TabPane } = Tabs;

enum PANE_KEY {
  FileLog = 'File-log',
  FolderPermissionsLog = 'Permissions-log',
  FolderLog = 'Folder-log',
}

interface LogModalProps {
  state: boolean;
  onCancel: () => void;
}

export enum LOG_ACTION {
  CREATED = 'log-action-created',
  ITEM_ADDED = 'log-action-item-added',
  CHANGED = 'log-action-changed',
  REQUESTED = 'log-action-requested',
  MOVED = 'log-action-moved',
  REMOVED = 'log-action-removed',
}

const LogModal = ({ state, onCancel }: LogModalProps) => {
  const { currentTheme } = useThemeSwitcher();
  const [activePane, setActivePane] = useState<PANE_KEY>(PANE_KEY.FileLog);

  const projectId = useProjectId();

  const theme = doReturnTheme(currentTheme);
  const intl = useIntl();

  const style = {
    container: {},
    titleBtn: {
      color: theme.colors.secondary,
      fontSize: '20px',
    },
    closeBtn: {
      color: theme.colors.darkGray,
      marginTop: '18px',
      fontSize: '20px',
      transition: '0.3s',
    },
  };

  const onExport = async () => {
    if (activePane === PANE_KEY.FileLog) {
      try {
        const csvData = await loadFilesActionLog('fileActionLog', {
          projectId: Number(projectId),
          response_type: 'csv',
          before: moment().unix(),
        });
        exportCsv(csvData.logs);
      } catch (err) {
        appLog('exportCsvErr => ', err);
      }
    }

    if (activePane === PANE_KEY.FolderLog) {
      try {
        const csvData = await loadFoldersLog('foldersActionLog', {
          projectId: Number(projectId),
          before: moment().unix(),
        });
        // exportCsv(csvData.logs);
      } catch (err) {
        appLog('exportCsvErr => ', err);
      }
    }

    if (activePane === PANE_KEY.FolderPermissionsLog) {
      try {
        const csvData = await loadFolderPermissionLog('folderPermissionLog', {
          projectId: Number(projectId),
          response_type: 'csv',
          before: moment().unix(),
        });
        exportCsv(csvData.logs);
      } catch (err) {
        appLog('exportCsvErr => ', err);
      }
    }
  };

  const exportCsv = (csvContent: string) => {
    const CsvData = 'data:text/csv;charset=utf-8,' + csvContent;
    const encodedUri = encodeURI(CsvData);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${activePane}-${moment.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
          }}>
          {icon('far', 'file-alt', style.titleBtn)}
          {intl.formatMessage({ id: 'log' })}
        </div>
      }
      bodyStyle={{ height: 'min(65vh, 600px)' }}
      centered
      visible={state}
      onOk={() => onExport()}
      onCancel={onCancel}
      okText={intl.formatMessage({ id: 'export' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      closeIcon={icon('far', 'times', style.closeBtn, 'close')}
      width="min(95vw, 1150px)">
      <div
        style={{
          marginTop: '-40px',
        }}>
        <Tabs
          defaultActiveKey={PANE_KEY.FileLog}
          onChange={(activeKey) => setActivePane(activeKey as PANE_KEY)}>
          <TabPane
            tab={intl.formatMessage({ id: 'files-log' })}
            key={PANE_KEY.FileLog}>
            <FileLog state before={moment().unix()} />
          </TabPane>
          <TabPane
            tab={intl.formatMessage({ id: 'folder-log' })}
            key={PANE_KEY.FolderLog}>
            <FolderLog state before={moment().unix()} />
          </TabPane>
          <TabPane
            tab={intl.formatMessage({ id: 'user-rights-log' })}
            key={PANE_KEY.FolderPermissionsLog}>
            <FolderPermissionsLog state before={moment().unix()} />
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default LogModal;
